/**
 * SERVER STATE LIVES IN TANSTACK QUERY, AND NOWHERE ELSE.
 *
 * - A 4xx is an answer, not a flake: it is never retried. A network failure,
 *   a timeout, a 5xx or a 429 is retried twice.
 * - A mutation is never retried automatically. A person retries a write on a
 *   screen that says what happened. The client sends no idempotency key.
 * - A 401 from any query or mutation is reported to the handler the app
 *   registered. That handler ends an active session and leaves a sign-in
 *   refusal alone (`noteUnauthenticated`).
 */
import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { isAppError, isRetryable } from "@/api/problems";
import { currentSessionEpoch } from "@/lib/auth/session";

let onUnauthenticated: (() => void) | undefined;

/** The app registers what "your session has ended" does. */
export function setUnauthenticatedHandler(handler: () => void): void {
  onUnauthenticated = handler;
}

function reportUnauthenticated(error: unknown) {
  if (isAppError(error) && error.kind === "unauthenticated") onUnauthenticated?.();
}

export function createQueryClient(): QueryClient {
  const mutationEpochs = new WeakMap<object, number>();
  return new QueryClient({
    queryCache: new QueryCache({ onError: reportUnauthenticated }),
    mutationCache: new MutationCache({
      onMutate: (_variables, mutation) => {
        mutationEpochs.set(mutation, currentSessionEpoch());
      },
      onError: (error, _variables, _context, mutation) => {
        if (mutationEpochs.get(mutation) === currentSessionEpoch()) reportUnauthenticated(error);
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        retry: (failureCount, error) => failureCount < 2 && isRetryable(error),
        refetchOnWindowFocus: true,
      },
      mutations: {
        retry: false,
        // Feedback stays while observed; submitted data leaves with its form.
        gcTime: 0,
      },
    },
  });
}
